import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageSquareWarning, Camera, Loader2, Send } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useFlags } from "@/hooks/useFlags";

const KEUZES = ["Er gaat iets mis", "Gegevens kloppen niet", "Dit kan beter", "Ik heb een vraag"];
const fouten = [];
let origConsoleError = null;

export function vangFouten() {
  if (origConsoleError) return;
  origConsoleError = console.error;
  console.error = (...a) => { fouten.push({ op: new Date().toISOString(), tekst: a.map((x) => (x?.message || String(x))).join(" ").slice(0, 300) }); if (fouten.length > 20) fouten.shift(); origConsoleError(...a); };
  window.addEventListener("error", (e) => fouten.push({ op: new Date().toISOString(), tekst: String(e.message).slice(0, 300) }));
  window.addEventListener("unhandledrejection", (e) => fouten.push({ op: new Date().toISOString(), tekst: String(e.reason?.message || e.reason).slice(0, 300) }));
}

export function contextNu(pathname) {
  const tab = new URLSearchParams(window.location.search).get("tab");
  const m = pathname.match(/\/([a-f0-9-]{20,}|\d{3,})(?:\/|$)/);
  return { route: pathname, tab, record_id: m?.[1] || null, record_type: pathname.includes("klantdossier") ? "klantdossier" : pathname.includes("klant") ? "klant" : pathname.includes("factu") ? "factuur" : null,
    fouten: fouten.slice(-5), scherm: `${window.innerWidth}×${window.innerHeight}`, versie: window.__bptVersie || null, offline: !navigator.onLine };
}

async function schermafbeelding() {
  try {
    const { default: html2canvas } = await import("html2canvas");
    const c = await html2canvas(document.body, { scale: 0.6, logging: false, useCORS: true, ignoreElements: (el) => el.getAttribute?.("data-testid") === "meldknop-dialoog" });
    return c.toDataURL("image/png");
  } catch { return null; }
}

export default function MeldKnop() {
  const { aan } = useFlags();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [keuze, setKeuze] = useState("");
  const [tekst, setTekst] = useState("");
  const [metFoto, setMetFoto] = useState(true);
  const [bezig, setBezig] = useState(false);
  const [resultaat, setResultaat] = useState(null);
  const ref = useRef(null);
  useEffect(() => { vangFouten(); }, []);
  useEffect(() => { if (open) setTimeout(() => ref.current?.focus(), 50); else { setResultaat(null); setKeuze(""); setTekst(""); } }, [open]);
  if (!aan("meldknop_v1")) return null;
  const verstuur = async () => {
    if (!keuze && !tekst.trim()) { toast.error("Kies wat er aan de hand is of beschrijf het kort"); return; }
    setBezig(true);
    try {
      const screenshot = metFoto ? await schermafbeelding() : null;
      const r = await api.post("/meldknop", { keuze: keuze || "Er gaat iets mis", tekst, context: contextNu(pathname), screenshot });
      setResultaat(r.data);
      toast.success(r.data.gekoppeld_aan ? `Gekoppeld aan bestaande melding ${r.data.nummer}` : `Melding ${r.data.nummer} aangemaakt — je krijgt bericht bij elke stap`);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); } finally { setBezig(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} title="Melden: fout, verkeerde gegevens, idee of vraag" className="fixed bottom-[calc(env(safe-area-inset-bottom)+140px)] md:bottom-24 right-4 md:right-6 z-[44] h-11 w-11 rounded-full bg-[#0D3B66] text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform" data-testid="meldknop">
        <MessageSquareWarning className="w-5 h-5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="meldknop-dialoog">
          <DialogHeader><DialogTitle>Iets melden</DialogTitle><DialogDescription className="text-xs">Scherm, tabblad, rol en de laatste fouten gaan automatisch mee. Je krijgt bericht bij elke stap.</DialogDescription></DialogHeader>
          {resultaat ? (
            <div className="space-y-2 text-sm" data-testid="meldknop-resultaat">
              {resultaat.gekoppeld_aan ? <div><b>Al bekend</b> — gekoppeld aan {resultaat.nummer}: {resultaat.titel}. {resultaat.ook_gemeld_door} collega('s) meldden dit ook.</div>
                : <div><b>{resultaat.nummer}</b> aangemaakt: {resultaat.titel}<div className="text-xs text-slate-500 mt-1">{resultaat.soort} · {resultaat.onderdeel} · {resultaat.ernst?.replace("_", " ")}{resultaat.toegewezen_aan_id ? " · als taak bij de ICT-eigenaar" : resultaat.soort === "verbetering" ? " · in de directie-backlog" : ""}</div>
                  {resultaat.antwoord_handboek && <div className="mt-2 p-2 rounded bg-emerald-50 text-emerald-900 text-xs" data-testid="meldknop-antwoord"><b>Antwoord:</b> {resultaat.antwoord_handboek}</div>}</div>}
              <div className="flex justify-end gap-2"><Link to="/meldingen" onClick={() => setOpen(false)} className="text-xs underline self-center" data-testid="meldknop-naar-meldingen">Mijn meldingen</Link><Button size="sm" onClick={() => setOpen(false)} data-testid="meldknop-sluiten">Sluiten</Button></div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-1.5">{KEUZES.map((k) => <button key={k} onClick={() => setKeuze(k)} className={`h-10 rounded border text-sm ${keuze === k ? "bg-[#0D3B66] text-white border-[#0D3B66]" : "bg-white hover:bg-slate-50"}`} data-testid={`meldknop-keuze-${k.replace(/\s+/g, "-").toLowerCase()}`}>{k}</button>)}</div>
              <textarea ref={ref} value={tekst} onChange={(e) => setTekst(e.target.value)} rows={4} placeholder="Wat zag je, wat verwachtte je? (vrije tekst — AI maakt er een nette melding van)" className="w-full border rounded p-2 text-sm" data-testid="meldknop-tekst" />
              <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={metFoto} onChange={(e) => setMetFoto(e.target.checked)} data-testid="meldknop-foto" /><Camera className="w-3.5 h-3.5" /> Schermafbeelding meesturen</label>
              <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={() => setOpen(false)}>Annuleren</Button><Button size="sm" disabled={bezig} onClick={verstuur} data-testid="meldknop-verstuur">{bezig ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" />Versturen</>}</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
